import { useRef } from "react";
import { CalendarDays } from "lucide-react";
import { FIND_RIDE_COLORS } from "../constants/findRide.constants";
import { tFindRide } from "../locales/FindRideTranslations";
import { formatDisplayDate } from "../utils/dateHelpers";

type FindRideDateFieldProps = {
  value: string;
};

/** Read-only date display — picker control sits beside the date chips. */
export function FindRideDateField({ value }: FindRideDateFieldProps) {
  return (
    <div className="flex min-w-0 items-center rounded-xl border border-gray-200 bg-white px-3 py-2.5">
      <CalendarDays
        className="mr-2 h-4 w-4 shrink-0 sm:mr-3"
        style={{ color: FIND_RIDE_COLORS.pinGreen }}
        aria-hidden
      />
      <span className="min-w-0 truncate text-sm text-gray-900">
        {formatDisplayDate(value)}
      </span>
    </div>
  );
}

type FindRideCalendarChipProps = {
  value: string;
  min: string;
  disabled?: boolean;
  onChange: (isoDate: string) => void;
};

/**
 * Calendar control beside date chips.
 * Uses an opacity-0 native `input[type=date]` overlay so the picker opens
 * reliably on mobile (sr-only + showPicker often fails on iOS/Android).
 */
export function FindRideCalendarChip({
  value,
  min,
  disabled = false,
  onChange,
}: FindRideCalendarChipProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    if (disabled) return;
    const input = inputRef.current;
    if (!input) return;
    try {
      if (typeof input.showPicker === "function") {
        input.showPicker();
      }
    } catch {
      /* Overlay input still receives the tap on most mobile browsers. */
    }
  };

  return (
    <div className="relative h-9 w-9 shrink-0 sm:h-[34px] sm:w-[34px]">
      <span
        className="pointer-events-none absolute inset-0 inline-flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600"
        aria-hidden
      >
        <CalendarDays className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <input
        ref={inputRef}
        type="date"
        value={value}
        min={min}
        disabled={disabled}
        onChange={(e) => {
          if (e.target.value) onChange(e.target.value);
        }}
        onClick={openPicker}
        className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        aria-label={tFindRide("pickDate")}
      />
    </div>
  );
}
